// @flow

import React, {useState, useEffect, useRef} from "react";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import {Input, Button, Tooltip} from "antd";
import {ArrowsAltOutlined, ShrinkOutlined, AimOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";

import {MapContainer, FormItem, DisableMapContainer} from "./SingleMarkerMapStyles";
import {
    configureLeafletMarkerIcons,
    getTileLayerPreference,
    addTileLayerWithFallback,
    TURKEY_BOUNDS
} from "../../../utils/helpers";
import {TileLayerControl} from "../TileLayerControl";

const mapControlButtonStyle = {
    backgroundColor: "#fff",
    boxShadow: "0 2px 6px rgba(0,0,0,0.3)"
};

export const SingleMarkerMap = ({
    icon,
    onClick,
    center,
    addExternalInput,
    customButton,
    disabled,
    zoom,
    viewCenter,
    previewCenter,
    previewZoom,
    onRecenter,
    onExternalLink,
    height
}) => {
    const {t} = useTranslation(["singleMarkerMap"]);
    const [errorMessage, setErrorMessage] = useState("");
    const [isFullscreen, setIsFullscreen] = useState(false);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);
    const tileLayerRef = useRef(null);
    const clickHandlerRef = useRef(null);

    // Initialize map
    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) return;

        const lat = center?.lat || 41.0082;
        const lng = center?.lng || 28.9784;

        configureLeafletMarkerIcons(L);

        // Türkiye sınırları
        const turkeyBounds = L.latLngBounds(TURKEY_BOUNDS.southWest, TURKEY_BOUNDS.northEast);

        mapInstanceRef.current = L.map(mapRef.current, {
            maxBounds: turkeyBounds,
            maxBoundsViscosity: 1.0,
            minZoom: 7,
            maxZoom: 22
        }).setView([lat, lng], 15);

        tileLayerRef.current = addTileLayerWithFallback(
            L,
            mapInstanceRef.current,
            getTileLayerPreference()
        );

        // Store click handler
        clickHandlerRef.current = e => {
            if (onClick && !disabled) {
                onClick({lat: e.latlng.lat, lng: e.latlng.lng});
            }
        };

        mapInstanceRef.current.on("click", clickHandlerRef.current);

        return () => {
            if (mapInstanceRef.current) {
                if (clickHandlerRef.current) {
                    mapInstanceRef.current.off("click", clickHandlerRef.current);
                }
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []);

    // Update marker
    useEffect(() => {
        if (!mapInstanceRef.current) return;
        if (!center || !center.lat || !center.lng) return;

        if (markerRef.current) {
            mapInstanceRef.current.removeLayer(markerRef.current);
        }

        const iconHtml = ReactDOMServer.renderToString(
            <div
                style={{
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#021529",
                    borderRadius: "50%",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    border: "2px solid #fff",
                    color: "#fff",
                    fontSize: "16px"
                }}
            >
                {icon}
            </div>
        );

        const customIcon = L.divIcon({
            html: iconHtml,
            className: "single-marker-custom-icon",
            iconSize: [30, 30],
            iconAnchor: [15, 15]
        });

        markerRef.current = L.marker([center.lat, center.lng], {
            icon: customIcon
        }).addTo(mapInstanceRef.current);

        const viewLat = viewCenter?.lat || center.lat;
        const viewLng = viewCenter?.lng || center.lng;
        const zoomLevel = zoom !== undefined ? zoom : 15;
        mapInstanceRef.current.setView([viewLat, viewLng], zoomLevel);
    }, [center?.lat, center?.lng, zoom, viewCenter?.lat, viewCenter?.lng]);

    // Önizleme: marker'ı (kayıtlı konumu) taşımadan yalnızca görünümü kaydırır
    useEffect(() => {
        if (!mapInstanceRef.current) return;
        if (!previewCenter?.lat || !previewCenter?.lng) return;
        mapInstanceRef.current.setView(
            [previewCenter.lat, previewCenter.lng],
            previewZoom !== undefined ? previewZoom : 15
        );
    }, [previewCenter?.lat, previewCenter?.lng, previewZoom]);

    const handleRecenter = () => {
        mapInstanceRef.current.setView([center.lat, center.lng], zoom !== undefined ? zoom : 15);
        onRecenter();
    };

    // Update onClick and disabled when they change
    useEffect(() => {
        if (!mapInstanceRef.current || !clickHandlerRef.current) return;

        // Update the handler function
        clickHandlerRef.current = e => {
            if (onClick && !disabled) {
                onClick({lat: e.latlng.lat, lng: e.latlng.lng});
            }
        };
    }, [onClick, disabled]);

    // Update disabled state
    useEffect(() => {
        if (!mapInstanceRef.current) return;

        if (disabled) {
            mapInstanceRef.current.dragging.disable();
            mapInstanceRef.current.touchZoom.disable();
            mapInstanceRef.current.doubleClickZoom.disable();
            mapInstanceRef.current.boxZoom.disable();
            mapInstanceRef.current.keyboard.disable();
        } else {
            mapInstanceRef.current.dragging.enable();
            mapInstanceRef.current.touchZoom.enable();
            mapInstanceRef.current.doubleClickZoom.enable();
            mapInstanceRef.current.boxZoom.enable();
            mapInstanceRef.current.keyboard.enable();
        }
    }, [disabled]);

    // Handle fullscreen mode
    useEffect(() => {
        if (!mapInstanceRef.current) return;

        // Invalidate size when fullscreen changes
        setTimeout(() => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.invalidateSize();
            }
        }, 100);
    }, [isFullscreen]);

    return (
        <div data-testid="singleMapMarker">
            {disabled && <DisableMapContainer height={height} data-testid="disableMap" />}
            <MapContainer
                disabled={disabled}
                height={height}
                data-testid="map"
                style={{
                    ...(isFullscreen && {
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 9999,
                        width: "100vw",
                        height: "100vh"
                    })
                }}
            >
                <div ref={mapRef} style={{width: "100%", height: "100%"}} />
                {/* Custom Button - Sol Alt */}
                {customButton && (
                    <div
                        style={{
                            position: "absolute",
                            bottom: "20px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 1000
                        }}
                    >
                        {customButton}
                    </div>
                )}
                {/* Sağ Üst Kontroller */}
                <div
                    style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        zIndex: 1000,
                        display: "flex",
                        gap: "8px"
                    }}
                >
                    {/* Tile Layer Control */}
                    {mapInstanceRef.current && (
                        <TileLayerControl
                            mapInstance={mapInstanceRef.current}
                            tileLayerRef={tileLayerRef}
                        />
                    )}
                    {/* Harita Büyütme Butonu */}
                    <Button
                        icon={isFullscreen ? <ShrinkOutlined /> : <ArrowsAltOutlined />}
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        style={mapControlButtonStyle}
                    />
                </div>
                {onRecenter && center?.lat && center?.lng && (
                    <div
                        style={{
                            position: "absolute",
                            bottom: "20px",
                            right: "10px",
                            zIndex: 1000
                        }}
                    >
                        <Tooltip
                            title={t("singleMarkerMap.recenter", {ns: "singleMarkerMap"})}
                            placement="left"
                        >
                            <Button
                                data-testid="recenterButton"
                                icon={<AimOutlined />}
                                onClick={handleRecenter}
                                style={mapControlButtonStyle}
                            />
                        </Tooltip>
                    </div>
                )}
            </MapContainer>

            {addExternalInput && (
                <FormItem help={errorMessage} validateStatus={errorMessage ? "warning" : "success"}>
                    <Input
                        disabled={disabled}
                        placeholder={t("singleMarkerMap.addExternalInput", {ns: "singleMarkerMap"})}
                        data-testid="addExternalInput"
                        onChange={event => {
                            setErrorMessage("");
                            const {value: address} = event.target;
                            const matchedAddress = address.match(
                                /dir\/([0-9.\,])+\/|@([0-9.])+\,([0-9.])+/g
                            );
                            const location = matchedAddress
                                ? matchedAddress[0].match(/[0-9.]+[0-9.]+/g)
                                : null;
                            if (location && location.length > 1) {
                                (onExternalLink || onClick)({
                                    lat: parseFloat(location[0]),
                                    lng: parseFloat(location[1])
                                });
                            } else {
                                setErrorMessage(
                                    address
                                        ? t("singleMarkerMap.externalInputError", {
                                              ns: "singleMarkerMap"
                                          })
                                        : ""
                                );
                            }
                        }}
                    />
                </FormItem>
            )}
        </div>
    );
};
